import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import parseAddress from '../utils/parseAddress';

const GOLD = '#B8892A';
const BD = '#E8E0D0';
const BG = '#FAFAF8';
const MUTED = '#6B5E4E';
const MUTED_LIGHT = '#A8998A';

const NAV_ITEMS_BASE = [
  { key: 'account',       icon: '👤', labelKey: 'profile.basicInfo' },
  { key: 'security',      icon: '🔒', labelKey: 'settings.changePassword' },
  { key: 'address',       icon: '📍', labelKey: 'profile.addresses' },
  { key: 'notifications', icon: '🔔', labelKey: 'notifications.title' },
  { key: 'preferences',   icon: '⚙', labelKey: 'settings.title' },
  { key: 'bidding',       icon: '🔨', labelKey: 'auction.title' },
  { key: 'danger',        icon: '⚠️', labelKey: 'common.tip' },
];

function SectionCard({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 4, overflow: 'hidden', marginBottom: 16, boxShadow: '0 1px 8px rgba(22,17,10,0.06)' }}>
      <div style={{ padding: '12px 20px', background: 'linear-gradient(135deg,#faf8f3,#f3ede3)', borderBottom: '1px solid ' + BD, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: 'var(--serif)', fontSize: 15, color: '#110E0A', letterSpacing: 2 }}>{title}</div>
          {subtitle && <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: MUTED_LIGHT, letterSpacing: 1, marginTop: 2 }}>{subtitle}</div>}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function SettingRow({ label, desc, value, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', borderBottom: '1px solid #F2EFE9', transition: 'background 0.12s' }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#FDFBF6'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: '#2E1E0A', marginBottom: 2 }}>{label}</div>
        {desc && <div style={{ fontSize: 11, color: MUTED_LIGHT, lineHeight: 1.6 }}>{desc}</div>}
      </div>
      {value && <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED }}>{value}</div>}
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );
}

function Toggle({ on, onChange }) {
  return (
    <div
      onClick={() => onChange(!on)}
      style={{
        width: 44, height: 24, borderRadius: 12, position: 'relative', cursor: 'pointer',
        background: on ? GOLD : '#E8DDD0', border: '1px solid ' + (on ? GOLD : BD),
        transition: 'all 0.2s', flexShrink: 0,
      }}
    >
      <div style={{
        position: 'absolute', width: 18, height: 18, borderRadius: '50%',
        background: '#fff', top: 2, left: on ? 22 : 2, transition: 'left 0.2s',
        boxShadow: '0 1px 4px rgba(22,17,10,0.2)',
      }} />
    </div>
  );
}

function EditBtn({ label = 'EDIT', danger, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      style={{
        padding: '5px 12px', border: '1px solid ' + (hover ? (danger ? '#9B2335' : GOLD) : BD),
        borderRadius: 3, fontFamily: 'var(--mono)', fontSize: 9, cursor: 'pointer',
        background: 'transparent', letterSpacing: 1,
        color: hover ? (danger ? '#9B2335' : GOLD) : MUTED_LIGHT,
        transition: 'all 0.13s',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {label}
    </button>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, refreshUser } = useAuth();
  const [activeSection, setActiveSection] = useState('account');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const NAV_ITEMS = NAV_ITEMS_BASE.map(item => ({ ...item, label: t(item.labelKey) }));
  // 进入设置页时刷新 user，避免 localStorage 缓存的旧数据
  useEffect(() => { refreshUser?.(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Notification matrix: type × channel
  const [notifMatrix, setNotifMatrix] = useState({
    bid_won:    { app: true,  wechat: true,  sms: true },
    bid_lost:   { app: true,  wechat: true,  sms: false },
    auction_start: { app: true, wechat: false, sms: false },
    mandate_approved: { app: true, wechat: true, sms: false },
    payment_due: { app: true, wechat: true, sms: true },
    shipment:   { app: true,  wechat: true,  sms: false },
    inspection: { app: true,  wechat: false, sms: false },
    deposit:    { app: true,  wechat: true,  sms: false },
    system:     { app: true,  wechat: false, sms: false },
  });

  // Preference toggles
  const [prefToggles, setPrefToggles] = useState({
    auto_refresh: true,
    sound: false,
    compact_mode: false,
    jpy_primary: true,
  });

  // Display preferences with localStorage persistence
  const [displayPrefs, setDisplayPrefs] = useState(() => {
    try {
      const saved = localStorage.getItem('aucproxy_display_dropdowns');
      if (saved) return JSON.parse(saved);
    } catch {}
    return { currency: 'jpy_cny', timezone: 'jst', sort: 'end_asc', itemsPerPage: '24' };
  });

  const updateDisplayPref = (key, value) => {
    setDisplayPrefs(prev => {
      const next = { ...prev, [key]: value };
      localStorage.setItem('aucproxy_display_dropdowns', JSON.stringify(next));
      return next;
    });
    message.success(t('common.success'));
  };

  // Bidding preferences
  const [bidPrefs, setBidPrefs] = useState({
    timing: '10min',
    fail_alert: true,
    screenshot: true,
    category: 'all',
  });

  // Security score
  const securityItems = [
    { label: '登录密码', done: true },
    { label: '手机绑定', done: !!user?.phone },
    { label: '邮箱绑定', done: !!user?.email },
    { label: 'KYC认证', done: user?.kyc_status === 'approved' },
    { label: '两步验证', done: false },
  ];
  const securityScore = securityItems.filter(i => i.done).length * 20;

  // Address management
  const [addresses, setAddresses] = useState([
    { id: '1', name: user?.name || '用户', phone: user?.phone || '—', postal: '200120', address: '上海市浦东新区陆家嘴环路1088号\n中国金融信息中心 28F', isDefault: true },
  ]);
  const [addressModal, setAddressModal] = useState(null); // null | 'add' | address object
  const [addrForm, setAddrForm] = useState({ name: '', phone: '', postal: '', address: '' });
  const [addrPasteText, setAddrPasteText] = useState('');

  // Login notification toggle
  const [loginNotif, setLoginNotif] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSaveNotifPrefs = () => {
    localStorage.setItem('aucproxy_notif_prefs', JSON.stringify(notifMatrix));
    message.success(t('common.success'));
  };

  const handleSavePrefs = () => {
    localStorage.setItem('aucproxy_display_prefs', JSON.stringify(prefToggles));
    localStorage.setItem('aucproxy_display_dropdowns', JSON.stringify(displayPrefs));
    message.success(t('common.success'));
  };

  const handleSaveBidPrefs = () => {
    localStorage.setItem('aucproxy_bid_prefs', JSON.stringify(bidPrefs));
    message.success(t('common.success'));
  };

  const handleAddAddress = () => {
    if (!addrForm.name || !addrForm.phone || !addrForm.address) {
      message.error(t('common.required'));
      return;
    }
    const newAddr = { id: Date.now().toString(), ...addrForm, isDefault: addresses.length === 0 };
    setAddresses(prev => [...prev, newAddr]);
    setAddressModal(null);
    setAddrForm({ name: '', phone: '', postal: '', address: '' });
    message.success(t('common.success'));
  };

  const handleSetDefaultAddress = (id) => {
    setAddresses(prev => prev.map(a => ({ ...a, isDefault: a.id === id })));
    message.success(t('common.success'));
  };

  const handleDeleteAddress = (id) => {
    setAddresses(prev => prev.filter(a => a.id !== id));
    message.success(t('common.success'));
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'account':
        return (
          <SectionCard title={t('profile.basicInfo')} subtitle="PROFILE">
            <SettingRow label={t('profile.phone')} value={user?.phone || '—'}
              action={<EditBtn label={user?.phone ? '修改' : '绑定'} onClick={() => navigate('/complete-profile?redirect=' + encodeURIComponent('/settings'))} />} />
            <SettingRow label={t('profile.email')} value={user?.email || '—'}
              action={<EditBtn label={user?.email ? '修改' : '绑定'} onClick={() => navigate('/complete-profile?redirect=' + encodeURIComponent('/settings'))} />} />
            <SettingRow label={t('profile.name')} value={user?.name || '—'} action={<EditBtn onClick={() => message.info(t('common.tip'))} />} />
            <SettingRow label={t('profile.kycStatus')} value={user?.kyc_status === 'approved' ? '✓ ' + t('profile.kycVerified') : t('profile.kycNotSubmitted')} />
            <SettingRow label={t('profile.memberLevel')} value={user?.tier === 'vip' ? 'VIP' : t('membership.bronze')} />
            <SettingRow label={t('profile.memberSince')} value={user?.created_at ? new Date(user.created_at).toLocaleDateString('zh-CN', { timeZone: 'Asia/Shanghai' }) : '—'} />
          </SectionCard>
        );

      case 'notifications': {
        const NOTIF_ROWS = [
          { key: 'bid_won', label: '竞拍成功' },
          { key: 'bid_lost', label: '竞拍失败' },
          { key: 'auction_start', label: '拍卖开始' },
          { key: 'mandate_approved', label: '委托审批' },
          { key: 'payment_due', label: '待付款' },
          { key: 'shipment', label: '物流更新' },
          { key: 'inspection', label: '检品完成' },
          { key: 'deposit', label: '资金变动' },
          { key: 'system', label: '系统通知' },
        ];
        const CHANNELS = [
          { key: 'app', label: '站内' },
          { key: 'wechat', label: '微信' },
          { key: 'sms', label: '短信' },
        ];
        return (
          <SectionCard title={t('notifications.title')} subtitle="NOTIFICATIONS">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: 12, color: MUTED, fontWeight: 500, borderBottom: '1px solid ' + BD }}>{t('notifications.title')}</th>
                    {CHANNELS.map(ch => (
                      <th key={ch.key} style={{ padding: '10px 16px', textAlign: 'center', fontSize: 12, color: MUTED, fontWeight: 500, borderBottom: '1px solid ' + BD, width: 80 }}>{ch.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {NOTIF_ROWS.map(row => (
                    <tr key={row.key}>
                      <td style={{ padding: '10px 20px', fontSize: 13, color: '#110E0A', borderBottom: '1px solid #F2EFE9' }}>{row.label}</td>
                      {CHANNELS.map(ch => (
                        <td key={ch.key} style={{ padding: '10px 16px', textAlign: 'center', borderBottom: '1px solid #F2EFE9' }}>
                          <Toggle on={notifMatrix[row.key]?.[ch.key] ?? false} onChange={v => setNotifMatrix(prev => ({ ...prev, [row.key]: { ...prev[row.key], [ch.key]: v } }))} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '12px 20px', borderTop: '1px solid ' + BD, textAlign: 'right' }}>
              <button onClick={handleSaveNotifPrefs} style={{ padding: '8px 20px', background: GOLD, border: 'none', borderRadius: 3, cursor: 'pointer', color: '#fff', fontFamily: 'var(--sans)', fontSize: 13 }}>{t('common.save')}</button>
            </div>
          </SectionCard>
        );
      }

      case 'security':
        return (
          <>
            {/* Security score */}
            <SectionCard title="安全评分" subtitle="SECURITY SCORE">
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                  <div style={{ fontFamily: 'var(--mono)', fontSize: 32, fontWeight: 700, color: securityScore >= 80 ? '#2A6645' : securityScore >= 60 ? GOLD : '#9B2335' }}>
                    {securityScore}
                  </div>
                  <div style={{ fontSize: 12, color: MUTED }}>/ 100 分</div>
                </div>
                <div style={{ height: 8, background: '#F2EFE9', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
                  <div style={{ height: '100%', width: `${securityScore}%`, borderRadius: 4, background: securityScore >= 80 ? '#2A6645' : securityScore >= 60 ? GOLD : '#9B2335', transition: 'width 0.5s' }} />
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {securityItems.map(item => (
                    <span key={item.label} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '1px solid ' + (item.done ? 'rgba(42,102,69,0.2)' : BD), background: item.done ? 'rgba(42,102,69,0.06)' : 'transparent', color: item.done ? '#2A6645' : MUTED_LIGHT }}>
                      {item.done ? '✓' : '○'} {item.label}
                    </span>
                  ))}
                </div>
              </div>
            </SectionCard>
            <SectionCard title="登录安全" subtitle="LOGIN SECURITY">
              <SettingRow label="登录密码" desc="定期更换密码以保护账号安全" action={<EditBtn label="修改" onClick={() => navigate('/profile')} />} />
              <SettingRow label="两步验证" desc="登录后需短信/邮箱验证码" value="未开启" action={<Toggle on={false} onChange={() => message.info('两步验证设置功能开发中')} />} />
              <SettingRow label="登录设备" desc="当前 2 台设备已授权" action={<EditBtn label="管理" onClick={() => message.info('设备管理功能开发中')} />} />
              <SettingRow label="登录通知" desc="检测到新设备登录时发送通知" action={<Toggle on={loginNotif} onChange={(v) => { setLoginNotif(v); message.success(v ? '已开启登录通知' : '已关闭登录通知'); }} />} />
            </SectionCard>
            <SectionCard title="资金安全" subtitle="FUNDS SECURITY">
              <SettingRow label="支付密码" desc="大额充值和提现时需要验证支付密码" value="未设置" action={<EditBtn label="设置" onClick={() => message.info('支付密码设置功能开发中')} />} />
              <SettingRow label="单次充值限额" value="¥50,000" action={<EditBtn onClick={() => message.info('充值限额设置功能开发中')} />} />
            </SectionCard>
          </>
        );

      case 'preferences': {
        const selectStyle = { padding: '6px 10px', border: '1px solid ' + BD, borderRadius: 3, background: '#fff', fontFamily: 'var(--sans)', fontSize: 12, color: MUTED, cursor: 'pointer', outline: 'none', transition: 'border 0.14s' };
        return (
          <SectionCard title="显示偏好" subtitle="DISPLAY">
            <SettingRow label="货币显示" desc="拍品价格同时显示人民币估算" action={
              <select value={displayPrefs.currency} onChange={e => updateDisplayPref('currency', e.target.value)} style={selectStyle}
                onFocus={e => { e.currentTarget.style.borderColor = GOLD; }}
                onBlur={e => { e.currentTarget.style.borderColor = BD; }}
              >
                <option value="jpy_cny">JPY + CNY 估算</option>
                <option value="jpy">仅 JPY</option>
                <option value="cny">折算 CNY</option>
              </select>
            } />
            <SettingRow label="拍卖行时区" desc="竞拍时间以所选时区显示" action={
              <select value={displayPrefs.timezone} onChange={e => updateDisplayPref('timezone', e.target.value)} style={selectStyle}
                onFocus={e => { e.currentTarget.style.borderColor = GOLD; }}
                onBlur={e => { e.currentTarget.style.borderColor = BD; }}
              >
                <option value="jst">JST（日本标准时间）</option>
                <option value="cst">CST（北京时间）</option>
              </select>
            } />
            <SettingRow label="拍品默认排序" action={
              <select value={displayPrefs.sort} onChange={e => updateDisplayPref('sort', e.target.value)} style={selectStyle}
                onFocus={e => { e.currentTarget.style.borderColor = GOLD; }}
                onBlur={e => { e.currentTarget.style.borderColor = BD; }}
              >
                <option value="end_asc">结拍时间（最近）</option>
                <option value="price_asc">起拍价（低到高）</option>
                <option value="price_desc">起拍价（高到低）</option>
                <option value="new">最新上架</option>
              </select>
            } />
            <SettingRow label="每页展示数量" action={
              <select value={displayPrefs.itemsPerPage} onChange={e => updateDisplayPref('itemsPerPage', e.target.value)} style={selectStyle}
                onFocus={e => { e.currentTarget.style.borderColor = GOLD; }}
                onBlur={e => { e.currentTarget.style.borderColor = BD; }}
              >
                <option value="12">12 件</option>
                <option value="24">24 件</option>
                <option value="48">48 件</option>
              </select>
            } />
            <SettingRow label="倒计时声音提醒" desc="拍卖结束前 1 分钟播放提示音" action={
              <Toggle on={prefToggles.sound} onChange={(v) => setPrefToggles((prev) => ({ ...prev, sound: v }))} />
            } />
            {[
              { key: 'auto_refresh', label: '自动刷新委托状态', desc: '每30秒自动刷新委托和订单状态' },
              { key: 'compact_mode', label: '紧凑模式', desc: '减少间距，在小屏幕上显示更多内容' },
            ].map(({ key, label, desc }) => (
              <SettingRow
                key={key} label={label} desc={desc}
                action={<Toggle on={prefToggles[key]} onChange={(v) => setPrefToggles((prev) => ({ ...prev, [key]: v }))} />}
              />
            ))}
            <SettingRow label="界面语言" value="中文" action={<EditBtn onClick={() => message.info('语言切换功能开发中')} />} />
            <SettingRow label="汇率刷新" desc="日元/人民币汇率更新频率" value="每小时" action={<EditBtn onClick={() => message.info('汇率刷新频率设置功能开发中')} />} />
            <div style={{ padding: '12px 20px', borderTop: '1px solid ' + BD, textAlign: 'right' }}>
              <button onClick={handleSavePrefs} style={{ padding: '8px 20px', background: GOLD, border: 'none', borderRadius: 3, cursor: 'pointer', color: '#fff', fontFamily: 'var(--sans)', fontSize: 13 }}>{t('common.save')}</button>
            </div>
          </SectionCard>
        );
      }

      case 'bidding':
        return (
          <SectionCard title="竞拍偏好" subtitle="BIDDING PREFERENCES">
            <SettingRow label="默认出价时机" desc="系统自动在结拍前指定时间出价" action={
              <select value={bidPrefs.timing} onChange={e => setBidPrefs(p => ({ ...p, timing: e.target.value }))} style={{ padding: '5px 10px', border: '1px solid ' + BD, borderRadius: 3, fontSize: 12, outline: 'none', fontFamily: 'var(--mono)' }}>
                <option value="10min">结拍前 10 分钟</option>
                <option value="3min">结拍前 3 分钟</option>
                <option value="1min">结拍前 1 分钟</option>
              </select>
            } />
            <SettingRow label="竞拍失败提醒" desc="未中标时立即推送提醒" action={<Toggle on={bidPrefs.fail_alert} onChange={v => setBidPrefs(p => ({ ...p, fail_alert: v }))} />} />
            <SettingRow label="竞拍截图保存" desc="自动保存出价截图作为凭证" action={<Toggle on={bidPrefs.screenshot} onChange={v => setBidPrefs(p => ({ ...p, screenshot: v }))} />} />
            <SettingRow label="偏好品类" desc="首页优先展示的品类" action={
              <select value={bidPrefs.category} onChange={e => setBidPrefs(p => ({ ...p, category: e.target.value }))} style={{ padding: '5px 10px', border: '1px solid ' + BD, borderRadius: 3, fontSize: 12, outline: 'none', fontFamily: 'var(--mono)' }}>
                <option value="all">全部品类</option>
                <option value="watch">腕表</option>
                <option value="jewelry">珠宝</option>
                <option value="bag">名包</option>
              </select>
            } />
            <div style={{ padding: '12px 20px', borderTop: '1px solid ' + BD, textAlign: 'right' }}>
              <button onClick={handleSaveBidPrefs} style={{ padding: '8px 20px', background: GOLD, border: 'none', borderRadius: 3, cursor: 'pointer', color: '#fff', fontFamily: 'var(--sans)', fontSize: 13 }}>{t('common.save')}</button>
            </div>
          </SectionCard>
        );

      case 'address':
        return (
          <SectionCard title={t('profile.addresses')} subtitle="DELIVERY ADDRESS">
            <div style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                {addresses.map(addr => (
                  <div key={addr.id} style={{
                    padding: 16, borderRadius: 6, position: 'relative',
                    border: '1px solid ' + (addr.isDefault ? GOLD : BD),
                    background: addr.isDefault ? 'rgba(184,137,42,0.03)' : 'transparent',
                    transition: 'all 0.14s',
                  }}>
                    {addr.isDefault && <span style={{ position: 'absolute', top: 12, right: 12, fontFamily: 'var(--mono)', fontSize: 9, padding: '2px 8px', borderRadius: 2, background: 'rgba(184,137,42,0.08)', border: '1px solid rgba(184,137,42,0.2)', color: GOLD, letterSpacing: 1 }}>默认</span>}
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#110E0A', marginBottom: 4 }}>{addr.name}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: MUTED_LIGHT, marginBottom: 8 }}>{addr.phone}</div>
                    <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.7, whiteSpace: 'pre-line' }}>{addr.address}</div>
                    {addr.postal && <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: MUTED_LIGHT, marginTop: 4 }}>{addr.postal}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button onClick={() => { setAddrForm({ name: addr.name, phone: addr.phone, postal: addr.postal || '', address: addr.address }); setAddressModal(addr); }} style={{ padding: '4px 10px', border: '1px solid ' + BD, borderRadius: 3, background: 'transparent', fontSize: 11, color: MUTED, cursor: 'pointer', fontFamily: 'var(--mono)', letterSpacing: 0.5, transition: 'all 0.12s' }}>编辑</button>
                      {!addr.isDefault && (
                        <button onClick={() => handleSetDefaultAddress(addr.id)} style={{ padding: '4px 10px', border: '1px solid ' + BD, borderRadius: 3, background: 'transparent', fontSize: 11, color: MUTED, cursor: 'pointer', fontFamily: 'var(--mono)', letterSpacing: 0.5, transition: 'all 0.12s' }}>设为默认</button>
                      )}
                      {!addr.isDefault && (
                        <button onClick={() => handleDeleteAddress(addr.id)} style={{ padding: '4px 10px', border: '1px solid ' + BD, borderRadius: 3, background: 'transparent', fontSize: 11, color: '#9B2335', cursor: 'pointer', fontFamily: 'var(--mono)', letterSpacing: 0.5, transition: 'all 0.12s' }}>删除</button>
                      )}
                    </div>
                  </div>
                ))}
                <div style={{ padding: 20, border: '1.5px dashed ' + BD, borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: MUTED_LIGHT, minHeight: 100, transition: 'all 0.14s' }}
                  onClick={() => { setAddrForm({ name: '', phone: '', postal: '', address: '' }); setAddressModal('add'); }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.background = 'rgba(184,137,42,0.03)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ fontSize: 24 }}>+</span>
                  <span style={{ fontSize: 12, fontFamily: 'var(--mono)', letterSpacing: 1 }}>新增收货地址</span>
                </div>
              </div>
            </div>
            {/* Address modal */}
            {addressModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
                <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 6, padding: 28, maxWidth: 460, width: '94%', boxShadow: '0 3px 20px rgba(22,17,10,0.14)' }}>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: 17, fontWeight: 600, color: '#110E0A', letterSpacing: 2, marginBottom: 6 }}>{addressModal === 'add' ? '新增收货地址' : '编辑地址'}</div>
                  <div style={{ fontSize: 12, color: MUTED_LIGHT, marginBottom: 20, lineHeight: 1.7 }}>填写国内收货信息</div>
                  {/* Smart paste */}
                  <div style={{
                    marginBottom: 18, padding: 14, borderRadius: 6,
                    background: 'rgba(184,137,42,0.04)', border: '1px dashed rgba(184,137,42,0.3)',
                  }}>
                    <div style={{ fontSize: 12, color: GOLD, fontWeight: 500, marginBottom: 6 }}>智能粘贴</div>
                    <textarea
                      value={addrPasteText}
                      onChange={e => setAddrPasteText(e.target.value)}
                      placeholder={'粘贴一段地址文字，自动识别\n例: 张三 13800138000 广东省深圳市南山区科技南路18号'}
                      rows={2}
                      style={{
                        width: '100%', padding: '8px 10px', border: '1px solid ' + BD, borderRadius: 3,
                        fontSize: 12, outline: 'none', resize: 'none', boxSizing: 'border-box',
                      }}
                    />
                    <button onClick={() => {
                      const parsed = parseAddress(addrPasteText);
                      if (!parsed) { message.warning('未能识别地址信息'); return; }
                      const fullAddr = [parsed.province, parsed.city, parsed.district, parsed.detail].filter(Boolean).join('');
                      setAddrForm(p => ({
                        ...p,
                        name: parsed.name || p.name,
                        phone: parsed.phone || p.phone,
                        postal: parsed.postal || p.postal,
                        address: fullAddr || p.address,
                      }));
                      setAddrPasteText('');
                      message.success('地址已自动填入');
                    }} disabled={!addrPasteText.trim()} style={{
                      marginTop: 8, padding: '5px 14px', borderRadius: 3, border: 'none',
                      background: addrPasteText.trim() ? GOLD : BD, color: '#fff',
                      fontSize: 12, cursor: addrPasteText.trim() ? 'pointer' : 'default',
                      transition: 'background 0.15s',
                    }}>
                      识别并填入
                    </button>
                  </div>
                  {[
                    { key: 'name', label: '收件人', placeholder: '请输入收件人姓名' },
                    { key: 'phone', label: '手机号', placeholder: '请输入收件手机号' },
                  ].map(f => (
                    <div key={f.key} style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontSize: 12, color: MUTED, marginBottom: 5 }}>{f.label}</label>
                      <input value={addrForm[f.key]} onChange={e => setAddrForm(p => ({ ...p, [f.key]: e.target.value }))} placeholder={f.placeholder} style={{ width: '100%', padding: '10px 12px', border: '1px solid ' + BD, borderRadius: 3, fontSize: 13, outline: 'none', transition: 'border 0.15s' }}
                        onFocus={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(184,137,42,0.12)'; }}
                        onBlur={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.boxShadow = 'none'; }}
                      />
                    </div>
                  ))}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, color: MUTED, marginBottom: 5 }}>详细地址</label>
                    <textarea value={addrForm.address} onChange={e => setAddrForm(p => ({ ...p, address: e.target.value }))} placeholder="省市区 + 街道/楼栋/门牌号" rows={3} style={{ width: '100%', padding: '10px 12px', border: '1px solid ' + BD, borderRadius: 3, fontSize: 13, outline: 'none', resize: 'vertical', transition: 'border 0.15s' }}
                      onFocus={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(184,137,42,0.12)'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', fontSize: 12, color: MUTED, marginBottom: 5 }}>邮政编码</label>
                    <input value={addrForm.postal} onChange={e => setAddrForm(p => ({ ...p, postal: e.target.value }))} placeholder="6位邮政编码" maxLength={6} style={{ width: '100%', padding: '10px 12px', border: '1px solid ' + BD, borderRadius: 3, fontSize: 13, outline: 'none', fontFamily: 'var(--mono)', transition: 'border 0.15s' }}
                      onFocus={e => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(184,137,42,0.12)'; }}
                      onBlur={e => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.boxShadow = 'none'; }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => setAddressModal(null)} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid ' + BD, borderRadius: 3, cursor: 'pointer', fontSize: 13, color: MUTED, transition: 'all 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = MUTED; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; }}
                    >{t('common.cancel')}</button>
                    <button onClick={() => {
                      if (addressModal === 'add') handleAddAddress();
                      else {
                        setAddresses(prev => prev.map(a => a.id === addressModal.id ? { ...a, ...addrForm } : a));
                        setAddressModal(null);
                        message.success('地址已更新');
                      }
                    }} style={{ flex: 1, padding: 10, background: '#161208', border: '1px solid #161208', borderRadius: 3, cursor: 'pointer', fontSize: 13, color: '#fff', transition: 'all 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#2C2210'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#161208'; }}
                    >{t('common.save')}</button>
                  </div>
                </div>
              </div>
            )}
          </SectionCard>
        );

      case 'danger':
        return (
          <SectionCard title="危险操作" subtitle="DANGER ZONE">
            <SettingRow label="退出所有设备" desc="强制退出所有已登录的设备和浏览器" action={<EditBtn label="退出所有" danger onClick={() => { handleLogout(); message.success('已退出所有设备'); }} />} />
            <SettingRow label="暂停账户" desc="暂时停用账户，可随时恢复" action={<EditBtn label="暂停" danger onClick={() => message.warning('账户暂停功能开发中，请联系客服')} />} />
            <SettingRow label="退出登录" desc="退出当前账号，返回登录页" action={<EditBtn label="退出" danger onClick={handleLogout} />} />
            <SettingRow label="注销账户" desc="永久删除账户及所有数据，此操作不可逆" action={<EditBtn label="申请注销" danger onClick={() => message.warning('账户注销请联系客服微信 AucProxy_CS')} />} />
          </SectionCard>
        );

      default:
        return null;
    }
  };

  return (
    <div style={{ background: BG, minHeight: 'calc(100vh - 120px)', padding: '0 0 60px' }}>
      {/* Page header */}
      <div style={{ background: '#fff', borderBottom: '1px solid ' + BD, padding: isMobile ? '12px 16px' : '16px 32px' }}>
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: 3, color: GOLD, marginBottom: 4 }}>SETTINGS</div>
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 600, color: '#110E0A', letterSpacing: 2, margin: 0 }}>{t('settings.title')}</h1>
        </div>
      </div>

      <div style={{ maxWidth: 980, margin: '0 auto', padding: isMobile ? '0' : '28px 32px 0', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px 1fr', gap: isMobile ? 0 : 24, alignItems: 'start' }}>
        {/* Left nav */}
        {isMobile ? (
          <div style={{ background: '#fff', borderBottom: '1px solid ' + BD, display: 'flex', flexWrap: 'wrap' }}>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                onClick={() => setActiveSection(item.key)}
                style={{
                  flex: '0 0 25%', padding: '11px 8px', background: activeSection === item.key ? 'rgba(184,137,42,0.06)' : 'transparent', border: 'none',
                  borderBottom: `2px solid ${activeSection === item.key ? GOLD : 'transparent'}`,
                  cursor: 'pointer', fontSize: 11, color: activeSection === item.key ? GOLD : MUTED,
                  fontFamily: 'var(--sans)', whiteSpace: 'nowrap', transition: 'all 0.13s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                <span style={{ fontSize: 13 }}>{item.icon}</span>{item.label}
              </button>
            ))}
          </div>
        ) : (
        <div style={{ background: '#fff', border: '1px solid ' + BD, borderRadius: 4, overflow: 'hidden', boxShadow: '0 1px 8px rgba(22,17,10,0.06)', position: 'sticky', top: 20 }}>
          <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg,#faf8f3,#f3ede3)', borderBottom: '1px solid ' + BD, fontFamily: 'var(--serif)', fontSize: 14, color: '#110E0A', letterSpacing: 3 }}>
            设 置
          </div>
          {NAV_ITEMS.map((item) => (
            <div
              key={item.key}
              onClick={() => setActiveSection(item.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '11px 16px', cursor: 'pointer', transition: 'all 0.14s',
                borderLeft: '3px solid ' + (activeSection === item.key ? GOLD : 'transparent'),
                borderBottom: '1px solid #F2EFE9',
                background: activeSection === item.key ? '#FDFBF6' : 'transparent',
              }}
            >
              <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
              <span style={{ fontSize: 13, color: activeSection === item.key ? '#110E0A' : MUTED, fontWeight: activeSection === item.key ? 500 : 400 }}>{item.label}</span>
            </div>
          ))}
        </div>
        )}

        {/* Right content */}
        <div style={{ padding: isMobile ? '16px' : '0' }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
